'use client';
import { MicrosoftPickerErrorCode } from '#pplx/frontend/components/hooks/MicrosoftPickerErrorCode.js';
import { microsoftItemToPickerDocument } from '#pplx/frontend/components/hooks/toPickerDocument.js';
import { CHANNEL_ID, AUTH_TIMEOUT, MAX_PICKER_INIT_RETRIES, PICKER_INIT_TIMEOUT, MicrosoftPickerError, } from '#pplx/frontend/components/hooks/useMicrosoftPicker.js';
import { useBooleanFlagValue } from '#pplx/frontend/experimentation/hooks.js';
import { useCallback, useMemo, useRef } from 'react';
const microsoftPickerState = { isLoginPopupOpen: false, msalClient: null };
const createMicrosoftPickerError = (code, message) => new MicrosoftPickerError(code, message);
const getErrorCode = (error) => {
    if (typeof error !== 'object' || error === null || !('errorCode' in error))
        return undefined;
    const { errorCode } = error;
    return typeof errorCode === 'string' ? errorCode : undefined;
};
export const SOURCE_EXTENSIONS = [
    '.txt',
    '.pdf',
    '.docx',
    '.pptx',
    '.xlsx',
    '.md',
    '.csv',
];
export const ATTACHMENT_EXTENSIONS = [
    '.txt',
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.docx',
    '.pptx',
    '.xlsx',
    '.md',
    '.csv',
];
export const AV_EXTENSIONS = [
    '.mp3',
    '.wav',
    '.aiff',
    '.ogg',
    '.flac',
    '.mp4',
    '.mpeg',
    '.mov',
    '.avi',
    '.flv',
    '.mpg',
    '.webm',
    '.wmv',
    '.3gp',
];
export const useMicrosoftPicker = () => {
    const windowRef = useRef(null);
    const portRef = useRef(null);
    const tokenInFlightRef = useRef(null);
    const { variation: useSharedOAuthCallback } = useBooleanFlagValue({
        flag: 'microsoft-picker-shared-oauth-callback',
        defaultValue: false,
    });
    const redirectPath = useSharedOAuthCallback
        ? '/rest/connections/oauth_callback'
        : '/account/connectors';
    const redirectUri = typeof window === 'undefined' ? '' : `${window.location.origin}${redirectPath}`;
    const isWorkOrSchoolAccount = useCallback((accountIdentifier) => {
        // Microsoft work/school accounts have UUID format: 36 chars with 4 dashes
        // e.g., "12345678-1234-1234-1234-123456789012"
        // Personal accounts have different format (e.g., hex strings)
        return (!!accountIdentifier &&
            accountIdentifier.length === 36 &&
            accountIdentifier.split('-').length === 5);
    }, []);
    const toPickerTenantName = useCallback((connector, tenantName, webUrl, loginHint, accountIdentifier) => {
        // For SharePoint, try to derive tenant from webUrl if tenantName is missing
        if (!tenantName && connector === 'sharepoint' && webUrl) {
            const match = webUrl.match(/^https?:\/\/([^.]+)\.sharepoint\.com/i);
            if (match) {
                const derivedTenant = match[1];
                return derivedTenant;
            }
        }
        // For OneDrive work/school accounts, try to derive tenant from loginHint email
        // Use account_identifier to reliably detect work vs personal accounts
        if (!tenantName &&
            connector === 'onedrive' &&
            isWorkOrSchoolAccount(accountIdentifier) &&
            loginHint) {
            const emailMatch = loginHint.match(/@([^.]+)\./);
            if (emailMatch) {
                const derivedTenant = emailMatch[1];
                return derivedTenant + '-my'; // OneDrive uses {tenant}-my format
            }
        }
        if (!tenantName) {
            return null;
        }
        else if (connector === 'sharepoint') {
            return tenantName;
        }
        else {
            return tenantName + '-my';
        }
    }, [isWorkOrSchoolAccount]);
    const getAccessToken = useCallback(async (options) => {
        let lastAuthErrorCode = null;
        const pickerTenant = toPickerTenantName(options.connector, options.tenantName, options.webUrl, options.loginHint, options.accountIdentifier);
        if (!microsoftPickerState.msalClient) {
            const PublicClientApplication = (await import('@azure/msal-browser'))
                .PublicClientApplication;
            microsoftPickerState.msalClient =
                await PublicClientApplication.createPublicClientApplication({
                    auth: {
                        authority: pickerTenant
                            ? 'https://login.microsoftonline.com/common'
                            : 'https://login.microsoftonline.com/consumers',
                        clientId: options.clientId,
                        redirectUri,
                    },
                    cache: {
                        cacheLocation: 'sessionStorage',
                        storeAuthStateInCookie: true,
                    },
                });
        }
        const scopes = pickerTenant
            ? [`https://${pickerTenant}.sharepoint.com/.default`]
            : ['OneDrive.ReadWrite'];
        const authParams = {
            scopes: scopes,
            login_hint: options.loginHint,
            prompt: 'select_account',
            redirectUri,
        };
        let accessToken = null;
        let activeAccount = microsoftPickerState.msalClient.getActiveAccount();
        if (!activeAccount) {
            const allAccounts = microsoftPickerState.msalClient.getAllAccounts();
            activeAccount =
                allAccounts.find((account) => account.username === options.loginHint) ??
                    null;
        }
        // First try to obtain an access token silently
        if (activeAccount) {
            try {
                microsoftPickerState.msalClient.setActiveAccount(activeAccount);
                const cachedToken = await microsoftPickerState.msalClient.acquireTokenSilent(authParams);
                accessToken = cachedToken.accessToken;
            }
            catch {
                // Silent auth can fail when consent or session state has changed.
            }
        }
        // If there is no access token by this point, either there was no active account
        // or an access token could not be obtained silently from the active account.
        // Try interactive authentication by asking for explicit consent.
        if (!accessToken && !microsoftPickerState.isLoginPopupOpen) {
            try {
                microsoftPickerState.isLoginPopupOpen = true;
                const result = await microsoftPickerState.msalClient.loginPopup(authParams);
                microsoftPickerState.msalClient.setActiveAccount(result.account);
                const cachedToken = await microsoftPickerState.msalClient.acquireTokenSilent(authParams);
                accessToken = cachedToken.accessToken;
            }
            catch (interactiveAcquisitionError) {
                const errorCode = getErrorCode(interactiveAcquisitionError);
                if (errorCode === 'popup_window_error') {
                    lastAuthErrorCode = MicrosoftPickerErrorCode.POPUP_BLOCKED;
                }
            }
            finally {
                microsoftPickerState.isLoginPopupOpen = false;
            }
        }
        if (accessToken) {
            return accessToken;
        }
        else {
            throw createMicrosoftPickerError(lastAuthErrorCode ?? MicrosoftPickerErrorCode.AUTH, 'Access token not acquired');
        }
    }, [redirectUri, toPickerTenantName]);
    const getPickerSource = useCallback((options) => {
        const pickerTenant = toPickerTenantName(options.connector, options.tenantName, options.webUrl, options.loginHint, options.accountIdentifier);
        let filters = options.mode === 'source' ? SOURCE_EXTENSIONS : ATTACHMENT_EXTENSIONS;
        filters = options.isAudioVideoFilesEnabled ? filters.concat(AV_EXTENSIONS) : filters;
        const pivots = options.connector === 'sharepoint'
            ? { recent: true, oneDrive: false, sharedLibraries: true }
            : { recent: true, oneDrive: true, sharedLibraries: false };
        const params = {
            sdk: '8.0',
            messaging: {
                origin: window.location.origin,
                channelId: CHANNEL_ID,
            },
            authentication: {},
            entry: options.connector === 'sharepoint'
                ? {
                    sharePoint: options.webUrl
                        ? { byPath: { web: new URL(options.webUrl).pathname } }
                        : {},
                }
                : { oneDrive: {} },
            typesAndSources: {
                mode: options.mode === 'attachment' ? 'files' : 'all',
                filters,
                pivots,
            },
            selection: { mode: 'multiple' },
        };
        const query = new URLSearchParams({
            filePicker: JSON.stringify(params),
        });
        const baseUrl = options.webUrl
            ? `${new URL(options.webUrl).origin}/_layouts/15/FilePicker.aspx`
            : pickerTenant
                ? `https://${pickerTenant}.sharepoint.com/_layouts/15/FilePicker.aspx`
                : `https://onedrive.live.com/picker`;
        return `${baseUrl}?${query}`;
    }, [toPickerTenantName]);
    const openPicker = useCallback(async (options) => {
        const handleClose = (error) => {
            window.removeEventListener('message', windowListener);
            portRef.current?.removeEventListener('message', portListener);
            portRef.current?.close();
            windowRef.current?.close();
            error ? options.onError?.(error) : options.onCancel?.();
        };
        const startTokenAcquisition = (options) => {
            if (!tokenInFlightRef.current) {
                tokenInFlightRef.current = (async () => {
                    try {
                        return await getAccessToken(options);
                    }
                    finally {
                        tokenInFlightRef.current = null;
                    }
                })();
            }
            return tokenInFlightRef.current;
        };
        const getAccessTokenWithTimeout = async (options) => {
            // If a token acquisition is already in flight, await it without a timeout
            if (tokenInFlightRef.current) {
                return await tokenInFlightRef.current;
            }
            return await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(createMicrosoftPickerError(MicrosoftPickerErrorCode.TIMEOUT, 'Request timed out'));
                }, AUTH_TIMEOUT);
                startTokenAcquisition(options)
                    .then((result) => {
                    clearTimeout(timeoutId);
                    resolve(result);
                })
                    .catch((error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                });
            });
        };
        const portListener = async (message) => {
            switch (message.data.type) {
                case 'command':
                    portRef.current?.postMessage({
                        type: 'acknowledge',
                        id: message.data.id,
                    });
                    // eslint-disable-next-line no-case-declarations
                    const command = message.data.data;
                    switch (command.command) {
                        case 'authenticate':
                            try {
                                const accessToken = await getAccessTokenWithTimeout(options);
                                portRef.current?.postMessage({
                                    type: 'result',
                                    id: message.data.id,
                                    data: {
                                        result: 'token',
                                        token: accessToken,
                                    },
                                });
                            }
                            catch (error) {
                                handleClose(error);
                            }
                            break;
                        case 'pick':
                            portRef.current?.postMessage({
                                type: 'result',
                                id: message.data.id,
                                data: { result: 'success' },
                            });
                            options.onPicked?.(command.items);
                            handleClose();
                            break;
                        case 'close':
                            handleClose();
                            break;
                        default:
                            handleClose();
                            break;
                    }
                    break;
                // This message type requires no action
                case 'notification':
                    break;
            }
        };
        let pickerInitialized = false;
        const windowListener = async (event) => {
            if (event.source && event.source === windowRef.current) {
                const message = event.data;
                if (message.type === 'initialize' && message.channelId === CHANNEL_ID) {
                    pickerInitialized = true;
                    window.dispatchEvent(new CustomEvent('microsoft-picker-initialized'));
                    portRef.current = event.ports[0];
                    portRef.current.addEventListener('message', portListener);
                    portRef.current.start();
                    portRef.current.postMessage({ type: 'activate' });
                }
            }
        };
        let accessToken;
        try {
            // Don't use getAccessTokenWithTimeout() here
            // This is the first attempt to obtain an access token
            // so it's the most likely one to require user interaction
            accessToken = await startTokenAcquisition(options);
        }
        catch (error) {
            handleClose(error);
            return;
        }
        const frame = document.getElementById('microsoft-file-picker');
        if (!frame) {
            handleClose();
            return;
        }
        windowRef.current = frame.contentWindow;
        if (!windowRef?.current) {
            handleClose();
            return;
        }
        const submitPickerForm = (targetWindow) => {
            const form = targetWindow.document.createElement('form');
            form.setAttribute('action', getPickerSource(options));
            form.setAttribute('method', 'POST');
            const input = targetWindow.document.createElement('input');
            input.setAttribute('type', 'hidden');
            input.setAttribute('name', 'access_token');
            input.setAttribute('value', accessToken);
            form.appendChild(input);
            targetWindow.document.body.appendChild(form);
            form.submit();
        };
        let initRetryCount = 0;
        const startInitTimeout = () => {
            setTimeout(() => {
                if (pickerInitialized) {
                    return;
                }
                if (initRetryCount < MAX_PICKER_INIT_RETRIES) {
                    initRetryCount++;
                    // Reset the iframe to about:blank before retrying so it becomes
                    // same-origin again (the previous form submission navigated it to
                    // the cross-origin SharePoint domain).
                    const frame = document.getElementById('microsoft-file-picker');
                    if (frame) {
                        frame.src = 'about:blank';
                        frame.onload = () => {
                            frame.onload = null;
                            windowRef.current = frame.contentWindow;
                            if (windowRef.current) {
                                submitPickerForm(windowRef.current);
                            }
                        };
                    }
                    startInitTimeout();
                }
                else {
                    handleClose(createMicrosoftPickerError(MicrosoftPickerErrorCode.TIMEOUT, 'File picker failed to initialize'));
                }
            }, PICKER_INIT_TIMEOUT);
        };
        submitPickerForm(windowRef.current);
        window.addEventListener('message', windowListener);
        startInitTimeout();
    }, [getAccessToken, getPickerSource]);
    return useMemo(() => ({ openPicker, toPickerDocument: microsoftItemToPickerDocument }), [openPicker]);
};
//# sourceMappingURL=useMicrosoftPickerSupport.js.map